import { useQuery } from '@apollo/client';
import { chain, sortBy, uniqBy } from 'lodash';
import gql from 'graphql-tag';
import React, { useMemo } from 'react';

import { getCourseColumnKey, getCourseKeyAliases, getCourseLabel } from '../profile/trainings.utils';
import useTenants from './useTenants';

export const FIND_COURSES_BY_CAMPUS = gql`
  query findCoursesByCampus($campusCode: String!) {
    findCoursesByCampus(campusCode: $campusCode) {
      activityCode
      activityName
      classificationId
      classificationName
    }
  }
`;

const getCoursesInitialState = (courses) => {
  let data = {};
  courses.forEach((c) => {
    data = { ...data, [getCourseColumnKey(c)]: 'none' };
  });
  return data;
};

export default function useCampusCourses(campusCode, trainings = []) {
  const TENANTS = useTenants();

  const isCSU = useMemo(() => {
    return TENANTS.getTenantByCampusCode(campusCode)?.tenantCode === 'CSU';
  }, [TENANTS, campusCode]);

  const { data, loading, error } = useQuery(FIND_COURSES_BY_CAMPUS, {
    variables: { campusCode },
    skip: isCSU,
  });

  const rawCourses = isCSU
    ? (trainings || []).map((course) => ({
        activityCode: course.srcActivityCode,
        activityName: course.activityName,
        classificationId: course.classifId,
        classificationName: course.classifName,
      }))
    : data?.findCoursesByCampus || [];

  const campusCourses = sortBy(rawCourses, ['classificationName', 'activityName']);

  // Keyed by column, not by code: the same course under two classifications is two distinct
  // filter options, and labelling them by name alone would render them as duplicates.
  const coursesForFilter = uniqBy(
    campusCourses.map((course) => ({
      value: getCourseColumnKey(course),
      label: getCourseLabel(course),
      classificationName: course.classificationName,
    })),
    'value',
  );

  const classificationsForFilter = chain(campusCourses)
    .map((course) => course.classificationName)
    .filter((classification) => classification && classification.trim() !== '')
    .uniq()
    .orderBy((classification) => classification, 'asc')
    .map((classification) => ({ value: classification, label: classification }))
    .value();

  const coursesInitialState = getCoursesInitialState(campusCourses);
  const courseKeyAliases = getCourseKeyAliases(campusCourses);

  return {
    campusCourses,
    courseKeyAliases,
    coursesForFilter,
    classificationsForFilter,
    coursesInitialState,
    loading: isCSU ? false : loading,
    error: isCSU ? null : error,
  };
}
